import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { DomSanitizer, SafeStyle } from "@angular/platform-browser";
import { HelperService } from "@app/main/core/services/helper.service";
import { environment } from "@env/environment";

interface Image {
  imgSrc: any;
  backgroundImageSrc: any;
  file: any | null;
  isExpand: boolean;
  color?: { first: string; second: string };
}

interface Data {
  backgroundImage: Image;
  image: Image;
  buttonTitle: string;
  url: string;
}

@Component({
  selector: "image-form",
  templateUrl: "./image-form.component.html",
  styleUrls: ["./image-form.component.scss"],
})
export class ImageFormComponent implements OnChanges {
  @Input("data") data: Data;
  @Output() formDataChanged: EventEmitter<any> = new EventEmitter<any>();

  backgroundImage: Image = {
    imgSrc: "",
    backgroundImageSrc: "",
    file: null,
    isExpand: false,
    color: { first: "#efeeee", second: "#efeeee" },
  };

  image: Image = {
    imgSrc: "",
    backgroundImageSrc: "",
    file: null,
    isExpand: false,
  };
  buttonTitle: string = "";
  url: string = "";

  s3BucketUrl = environment.s3BucketUrl;

  constructor(
    private helperService: HelperService,
    private domSanitizer: DomSanitizer,
    private cdref: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Check if 'data' input has changed
    if (changes.data && changes.data.currentValue) {
      const newData = changes.data.currentValue;

      // Update local variables with data from 'data' input
      this.backgroundImage.imgSrc = newData.backgroundImage.imgSrc;
      this.backgroundImage.backgroundImageSrc =
        newData.backgroundImage.backgroundImageSrc;
      this.backgroundImage.file = newData.backgroundImage.file || null;
      this.backgroundImage.color.first =
        newData.backgroundImage.color.first || "#00000";
      this.backgroundImage.color.second =
        newData.backgroundImage.color.second || "#00000";
      this.backgroundImage.isExpand = newData.backgroundImage.isExpand;

      this.image.imgSrc = newData.image.imgSrc;
      this.image.backgroundImageSrc = newData.image.backgroundImageSrc;
      this.image.file = newData.image.file || null;
      this.image.isExpand = newData.image.isExpand;

      this.buttonTitle = newData.buttonTitle;
      this.url = newData.url;
    }
  }

  uploadImage = ($event, type: "image" | "backgroundImage", shouldCompress: boolean = true) => {
  if ($event && $event.target && $event.target.files.length !== 0) {
    let file = $event.target.files[0];

    const processImage = async (base64: string, blob: Blob) => {
      this[type].file = blob;
      this[type].imgSrc = await this.sanitizeImageUrl(base64);
      this[type].backgroundImageSrc =
        type === "image"
          ? this.sanitizeBackgroundImageUrl(base64)
          : base64;

      this.onLocalVariableChange();
      this.cdref.detectChanges();
    };

    if (shouldCompress) {
      this.helperService
        .compressImage(file)
        .then(async (result) => {
          await processImage(result.compressedBase64, result.compressedBlob);
        })
        .catch((error) => {
          console.error("Error compressing image:", error);
        });
    } else {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        await processImage(base64, file);
      };
      reader.readAsDataURL(file);
    }
  }
};


  sanitizeImageUrl(url: string): SafeStyle {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }

  sanitizeBackgroundImageUrl(url: string): SafeStyle {
    return this.domSanitizer.bypassSecurityTrustStyle(`url(${url})`);
  }

  getImageSrc(): any {
    return this.image.file !== null
      ? this.image.backgroundImageSrc
      : this.sanitizeBackgroundImageUrl(
          this.s3BucketUrl + this.image.backgroundImageSrc
        );
  }

  getBackgroundImageStyle(): SafeStyle {
    const color1 = this.backgroundImage.color.first || "transparent";
    const color2 = this.backgroundImage.color.second || "transparent";

    let imageUrl = null;

    if (this.backgroundImage.file !== null) {
      imageUrl = this.backgroundImage.backgroundImageSrc;
    } else if (this.backgroundImage.backgroundImageSrc !== "") {
      imageUrl = this.s3BucketUrl + this.backgroundImage.backgroundImageSrc;
    }

    const style = imageUrl
      ? `url(${imageUrl}), linear-gradient(to right, ${color1}, ${color2})`
      : `linear-gradient(to right, ${color1}, ${color2})`;

    return this.domSanitizer.bypassSecurityTrustStyle(style);
  }

  // Method to handle changes in local variables and sync with 'data' input
  onLocalVariableChange() {
    // Update 'data' input with current local variable values
    this.data = {
      backgroundImage: {
        imgSrc: this.backgroundImage.imgSrc,
        backgroundImageSrc: this.backgroundImage.backgroundImageSrc,
        file: this.backgroundImage.file,
        isExpand: this.backgroundImage.isExpand,
        color: {
          first: this.backgroundImage.color.first,
          second: this.backgroundImage.color.second,
        },
      },
      image: {
        imgSrc: this.image.imgSrc,
        backgroundImageSrc: this.image.backgroundImageSrc,
        file: this.image.file,
        isExpand: this.image.isExpand,
      },
      buttonTitle: this.buttonTitle,
      url: this.url,
    };

    this.formDataChanged.emit(this.data);
  }
}
